<script lang="ts">
    import {getInjector} from 'src/core';
    import {Flex, Spacer} from 'shared/layouts';
    import {popInAnim, popOutAnim} from 'shared/animations';
    import {KpGenericTopbar} from 'shared/components/kp-generic-topbar';
    import type {Thread} from 'src/features/threads/threads.types';
    import type {ThreadsContext} from 'src/features/threads/services/threads-context.service';
    import {ThreadsContextService} from 'src/features/threads/services/threads-context.service';
    import {createEventDispatcher, onDestroy} from 'svelte';
    import {cleanup} from 'shared/utils/custom-utils';
    import {flip} from 'svelte/animate';
    import {THREADS_VERSION} from 'src/features/threads/threads.constants';
    import KpSearchBar from 'shared/ui-widgets/search/KpSearchBar.svelte';
    import ThreadCard from 'src/features/threads/popover/components/ThreadCard.svelte';
    import KpHeading from 'shared/components/kp-heading/KpHeading.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import KpIconButton from 'shared/ui-widgets/button/KpIconButton.svelte';

    export let selectedThread: Thread | null;

    const contextService = getInjector().getByClass(ThreadsContextService);
    const dispatch = createEventDispatcher<{
        'new-thread': void,
        'close': void,
        'thread-select': Thread
    }>();

    let threadsContext: ThreadsContext;
    let searchQuery = '';
    const threadContextSubscription = contextService.context$.subscribe((currentThreadsContext) => threadsContext = currentThreadsContext);

    onDestroy(() => {
        cleanup(threadContextSubscription);
    });

    const handleSearch = (event: CustomEvent<string>) => {
        searchQuery = event.detail;
    };

    const handleSearchReset = () => {
        searchQuery = '';
    };

    $: filteredThreads = threadsContext.threads.filter((thread) => thread.name.toUpperCase().includes(searchQuery.toUpperCase()));
</script>

<div class="threads-sidebar">
    <KpGenericTopbar horizontalPadding="16px">
        <KpHeading type="h3">Konverzace</KpHeading>
        <Spacer flex="1"/>
        <KpIconButton icon="cross-circle" on:click={() => dispatch('close')}/>
    </KpGenericTopbar>

    <div class="sidebar-content">
        <KpSearchBar placeholder="{searchQuery.length > 0 ? `Vyhledáno: ${searchQuery}` : 'Hledat konverzace...'}"
                     forceResetButton={searchQuery.length > 0}
                     height="34px"
                     on:search={handleSearch}
                     on:reset={handleSearchReset}/>

        <Spacer direction="vertical" size="ml"/>

        <Flex direction="column" gap="sm" flexGrow={1}>
            {#each filteredThreads as thread(thread.id)}
                <div class="anim-container"
                     in:popInAnim={{key: thread.id}}
                     out:popOutAnim={{key: thread.id}}
                     animate:flip={{duration: 250}}>

                    <ThreadCard {thread}
                                selected={selectedThread?.id === thread.id}
                                on:click={() => dispatch('thread-select', thread)}/>
                </div>
            {/each}

            {#if filteredThreads.length === 0}
                <IconedContent align="center" justify="center" orientation="vertical" icon="info" fillAvailableSpace>
                    {#if searchQuery}
                        Žádné výsledky pro hledaný výraz '{searchQuery}'
                    {:else}
                        Nemáte žádné konverzace
                    {/if}
                </IconedContent>
            {/if}
        </Flex>

        <Spacer direction="vertical" size="ml"/>

        <KpButton isBlock buttonStyle="success" on:click={() => dispatch('new-thread')}>
            <IconedContent icon="add">Vytvořit novou konverzaci</IconedContent>
        </KpButton>

        <Spacer direction="vertical" size="sm"/>

        <small class="text-muted text-center">{THREADS_VERSION}</small>
    </div>
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";
    @import (reference) "styles/portaro-erp.less";

    .threads-sidebar {
        .flex-grow();
        overflow-y: auto;

        .sidebar-content {
            .flex-grow();
            padding: @spacing-ml;
        }
    }
</style>